import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Switch } from "../../../ui/components/Switch";
import { Checkbox } from "../../../ui/components/Checkbox";
import { TextInput } from "../../../ui/components/Form";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  createCommandDefinition,
  deleteCommandDefinition,
  fetchCommandDefinition,
  previewCommandPayload,
  updateCommandDefinition,
  type CommandComponentScope,
  type CommandDefinition,
  type CommandDefinitionInput,
  type CommandParamField,
  type CommandParamType,
} from "../../lib/api/commands";
import {
  CommandParamForm,
  initialParamValues,
  paramValuesToRequest,
  type CommandParamValues,
} from "../commands/CommandParamForm";
import { JsonBlock } from "../commands/CommandResponseView";
import * as s from "../commands/commands.css";
import * as cs from "./CommandCatalog.css";

const PARAM_TYPES: CommandParamType[] = [
  "string",
  "integer",
  "number",
  "boolean",
  "enum",
  "register",
  "register_list",
  "register_values",
];
const SCOPES: CommandComponentScope[] = ["ems", "pcs", "dcdc", "hvac", "pdu", "pmu"];

type ParamDraft = CommandParamField & { optionsText: string; defaultText: string };

interface Draft extends Omit<CommandDefinitionInput, "params" | "payloadTemplate"> {
  params: ParamDraft[];
  templateText: string;
  shadowName: string;
  shadowBuilder: "" | "sys_time";
  datumText: string;
}

const EMPTY_DRAFT: Draft = {
  key: "",
  iotDomain: "sanctuary",
  componentScope: null,
  category: "",
  displayName: "",
  description: "",
  transport: "mqtt",
  params: [],
  responseRender: "status",
  successPredicate: "st_if_present",
  timeoutMs: 10_000,
  dangerLevel: "normal",
  confirmationText: "",
  preconditions: {},
  preRead: null,
  sortOrder: 0,
  enabled: true,
  templateText: '{\n  "id": ""\n}',
  shadowName: "settings",
  shadowBuilder: "",
  datumText: "{}",
};

function toDraft(d: CommandDefinition): Draft {
  const template = d.payloadTemplate as Record<string, unknown>;
  const isShadow = d.transport === "shadow";
  return {
    ...d,
    description: d.description ?? "",
    confirmationText: d.confirmationText ?? "",
    params: d.params.map((p) => ({
      ...p,
      optionsText: (p.options ?? []).map((o) => `${o.value}=${o.label}`).join("\n"),
      defaultText: p.default == null ? "" : String(p.default),
    })),
    templateText: isShadow ? EMPTY_DRAFT.templateText : JSON.stringify(template, null, 2),
    shadowName: isShadow ? String(template.shadowName ?? "settings") : "settings",
    shadowBuilder: isShadow && template.builder === "sys_time" ? "sys_time" : "",
    datumText: isShadow ? JSON.stringify(template.datum ?? {}, null, 2) : "{}",
  };
}

function parseDefault(p: ParamDraft): unknown {
  const text = p.defaultText.trim();
  if (!text) return undefined;
  if (p.type === "boolean") return text === "true";
  if (p.type === "integer" || p.type === "number") return Number(text);
  return text;
}

function toParam(p: ParamDraft): CommandParamField {
  const { optionsText, defaultText: _d, ...rest } = p;
  const field: CommandParamField = { ...rest, default: parseDefault(p) };
  if (p.type === "enum") {
    field.options = optionsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [value, ...label] = line.split("=");
        return { value: value.trim(), label: (label.join("=") || value).trim() };
      });
  } else {
    delete field.options;
  }
  if (field.requires && !field.requires.wcmMinFw) delete field.requires;
  return field;
}

/** Builds the API body; returns an error key when the JSON fields don't parse. */
function toInput(draft: Draft): { input?: CommandDefinitionInput; errorKey?: string } {
  let payloadTemplate: Record<string, unknown>;
  try {
    if (draft.transport === "shadow") {
      payloadTemplate = draft.shadowBuilder
        ? { shadowName: draft.shadowName, builder: draft.shadowBuilder }
        : { shadowName: draft.shadowName, datum: JSON.parse(draft.datumText || "{}") };
    } else {
      payloadTemplate = JSON.parse(draft.templateText);
    }
  } catch {
    return { errorKey: "pro.commands.catalog.error.json" };
  }
  const { templateText: _t, shadowName: _s, shadowBuilder: _b, datumText: _dt, ...rest } = draft;
  return {
    input: {
      ...rest,
      componentScope: draft.iotDomain === "powersave" ? draft.componentScope : null,
      description: draft.description?.trim() || null,
      confirmationText: draft.confirmationText?.trim() || null,
      params: draft.params.map(toParam),
      payloadTemplate,
    },
  };
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <TextInput
      label={label}
      value={value == null ? "" : String(value)}
      inputMode="decimal"
      onChange={(e) => onChange(e.target.value.trim() === "" ? undefined : Number(e.target.value))}
    />
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <label className={s.field}>
      <span className={formStyles.label}>{label}</span>
      <select className={formStyles.input} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ParamEditor({
  param,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}: {
  param: ParamDraft;
  index: number;
  count: number;
  onChange: (p: ParamDraft) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  const set = (patch: Partial<ParamDraft>) => onChange({ ...param, ...patch });
  const numeric = param.type === "integer" || param.type === "number";
  return (
    <div className={cs.paramRow}>
      <div className={cs.paramRowHeader}>
        <span className={s.commandName}>{param.label || param.key || t("pro.commands.catalog.param.untitled")}</span>
        <div className={s.inlineRow}>
          <button
            type="button"
            className={s.iconButton}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t("pro.commands.catalog.param.moveUp")}
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            className={s.iconButton}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label={t("pro.commands.catalog.param.moveDown")}
          >
            <ArrowDown size={14} />
          </button>
          <button
            type="button"
            className={s.iconButton}
            onClick={onRemove}
            aria-label={t("pro.commands.catalog.param.remove")}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className={s.fieldGrid}>
        <TextInput label={t("pro.commands.catalog.param.key")} value={param.key} onChange={(e) => set({ key: e.target.value })} />
        <TextInput label={t("pro.commands.catalog.param.label")} value={param.label} onChange={(e) => set({ label: e.target.value })} />
        <Select
          label={t("pro.commands.catalog.param.type")}
          value={param.type}
          options={PARAM_TYPES.map((type) => ({ value: type, label: t(`pro.commands.paramType.${type}`) }))}
          onChange={(type) => set({ type })}
        />
        {numeric && (
          <>
            <NumberInput label={t("pro.commands.catalog.param.min")} value={param.min} onChange={(min) => set({ min })} />
            <NumberInput label={t("pro.commands.catalog.param.max")} value={param.max} onChange={(max) => set({ max })} />
            <TextInput label={t("pro.commands.catalog.param.unit")} value={param.unit ?? ""} onChange={(e) => set({ unit: e.target.value || undefined })} />
          </>
        )}
        {!param.type.startsWith("register_") && (
          <TextInput
            label={t("pro.commands.catalog.param.default")}
            value={param.defaultText}
            onChange={(e) => set({ defaultText: e.target.value })}
          />
        )}
        <TextInput
          label={t("pro.commands.catalog.param.minFw")}
          value={param.requires?.wcmMinFw ?? ""}
          placeholder="2.2.4"
          onChange={(e) => set({ requires: { ...param.requires, wcmMinFw: e.target.value || undefined } })}
        />
      </div>
      {param.type === "enum" && (
        <label className={s.field}>
          <span className={formStyles.label}>{t("pro.commands.catalog.param.options")}</span>
          <textarea
            className={s.textarea}
            style={{ minHeight: 70 }}
            value={param.optionsText}
            placeholder={"0=On-grid\n1=Off-grid"}
            onChange={(e) => set({ optionsText: e.target.value })}
          />
        </label>
      )}
      <TextInput label={t("pro.commands.catalog.param.help")} value={param.help ?? ""} onChange={(e) => set({ help: e.target.value || undefined })} />
      <div className={s.inlineRow} style={{ gap: 20 }}>
        <label className={s.inlineRow}>
          <Checkbox checked={Boolean(param.required)} onCheckedChange={(required) => set({ required })} />
          <span>{t("pro.commands.catalog.param.required")}</span>
        </label>
        <label className={s.inlineRow}>
          <Checkbox checked={Boolean(param.asString)} onCheckedChange={(asString) => set({ asString })} />
          <span>{t("pro.commands.catalog.param.asString")}</span>
        </label>
      </div>
    </div>
  );
}

function PreviewPanel({ draft, token }: { draft: Draft; token: string }) {
  const { t } = useI18n();
  const fields = React.useMemo(() => draft.params.map(toParam).filter((p) => p.key), [draft.params]);
  const fieldsKey = JSON.stringify(fields);
  const [values, setValues] = React.useState<CommandParamValues>(() => initialParamValues(fields));
  const [payload, setPayload] = React.useState<unknown>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => setValues(initialParamValues(fields)), [fieldsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const { input, errorKey } = toInput(draft);
    if (!input) {
      setError(t(errorKey!));
      return;
    }
    const id = setTimeout(() => {
      previewCommandPayload(token, input, paramValuesToRequest(fields, values))
        .then((res) => {
          setPayload(res.payload);
          setError(null);
        })
        .catch((err) => setError(err?.message ?? t("pro.commands.catalog.error.preview")));
    }, 400);
    return () => clearTimeout(id);
  }, [draft, values, fields, token, t]);

  return (
    <div className={cs.stickyPanel}>
      <span className={cs.panelTitle}>{t("pro.commands.catalog.preview.title")}</span>
      <span className={s.help}>{t("pro.commands.catalog.preview.help")}</span>
      <CommandParamForm fields={fields} values={values} onChange={setValues} />
      {error ? <div className={s.bannerDanger}>{error}</div> : <JsonBlock value={payload} />}
    </div>
  );
}

export function CommandCatalogDetailPage({ definitionId }: { definitionId: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();
  const isNew = definitionId === "new";

  const [draft, setDraft] = React.useState<Draft>(EMPTY_DRAFT);
  const [existing, setExisting] = React.useState<CommandDefinition | null>(null);
  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (isNew || !token) return;
    setLoading(true);
    fetchCommandDefinition(token, definitionId)
      .then((d) => {
        setExisting(d);
        setDraft(toDraft(d));
      })
      .catch((err) => setError(err?.message || t("pro.commands.catalog.error.load")))
      .finally(() => setLoading(false));
  }, [definitionId, isNew, token, t]);

  const set = (patch: Partial<Draft>) => {
    setSaved(false);
    setDraft((prev) => ({ ...prev, ...patch }));
  };
  const setParams = (params: ParamDraft[]) => set({ params });
  const setPre = (patch: Partial<Draft["preconditions"]>) => {
    const next = { ...draft.preconditions, ...patch };
    for (const key of Object.keys(next) as Array<keyof typeof next>) {
      const value = next[key];
      if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) delete next[key];
    }
    set({ preconditions: next });
  };
  const registerValueParams = draft.params.filter((p) => p.type === "register_values" && p.key);
  const back = () => navigate({ to: "/system/command-catalog" });

  const handleSave = async () => {
    if (!token) return;
    const { input, errorKey } = toInput(draft);
    if (!input) {
      setError(t(errorKey!));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await createCommandDefinition(token, input);
        navigate({ to: "/system/command-catalog/$definitionId", params: { definitionId: created.id }, replace: true });
      } else {
        const updated = await updateCommandDefinition(token, definitionId, input);
        setExisting(updated);
        setDraft(toDraft(updated));
        setSaved(true);
      }
    } catch (err: any) {
      setError(err?.message || t("pro.commands.catalog.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || isNew) return;
    const ok = await confirm(
      t("pro.commands.catalog.delete.title"),
      t("pro.commands.catalog.delete.body", { name: draft.displayName }),
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    try {
      await deleteCommandDefinition(token, definitionId);
      back();
    } catch (err: any) {
      setError(err?.message || t("pro.commands.catalog.error.delete"));
    }
  };

  if (loading) return <AuthTransitionScreen variant="fill" message={t("shared.common.loading")} />;

  return (
    <div className={cs.page}>
      <button type="button" className={cs.backLink} onClick={back}>
        <ArrowLeft size={14} /> {t("pro.commands.catalog.title")}
      </button>

      <div className={cs.header}>
        <div>
          <Text level="h1" weight="bold">
            {isNew ? t("pro.commands.catalog.new") : draft.displayName || draft.key}
          </Text>
          {existing && (
            <span className={s.muted}>
              {t("pro.commands.catalog.version", {
                version: existing.version,
                updated: new Date(existing.updatedAt).toLocaleString(),
              })}
            </span>
          )}
        </div>
        <Switch
          label={t("pro.commands.catalog.enabled")}
          checked={draft.enabled}
          onCheckedChange={(enabled) => set({ enabled })}
        />
      </div>

      {error && <Callout variant="error">{error}</Callout>}
      {saved && <Callout variant="success">{t("pro.commands.catalog.saved")}</Callout>}

      <div className={cs.layout}>
        <div className={s.field} style={{ gap: 16 }}>
          <section className={cs.panel}>
            <span className={cs.panelTitle}>{t("pro.commands.catalog.section.basics")}</span>
            <div className={s.fieldGrid}>
              <TextInput label={t("pro.commands.catalog.field.displayName")} value={draft.displayName} onChange={(e) => set({ displayName: e.target.value })} />
              <TextInput label={t("pro.commands.catalog.field.key")} value={draft.key} onChange={(e) => set({ key: e.target.value })} />
              <TextInput label={t("pro.commands.catalog.field.category")} value={draft.category} onChange={(e) => set({ category: e.target.value })} />
              <Select
                label={t("pro.commands.catalog.field.domain")}
                value={draft.iotDomain}
                options={[
                  { value: "sanctuary", label: t("pro.commands.domain.sanctuary") },
                  { value: "powersave", label: t("pro.commands.domain.powersave") },
                ]}
                onChange={(iotDomain) => set({ iotDomain })}
              />
              {draft.iotDomain === "powersave" && (
                <Select<CommandComponentScope | "">
                  label={t("pro.commands.catalog.field.scope")}
                  value={draft.componentScope ?? ""}
                  options={[
                    { value: "", label: t("pro.commands.catalog.field.scopeNone") },
                    ...SCOPES.map((scope) => ({ value: scope, label: scope.toUpperCase() })),
                  ]}
                  onChange={(scope) => set({ componentScope: scope || null })}
                />
              )}
              <NumberInput
                label={t("pro.commands.catalog.field.sortOrder")}
                value={draft.sortOrder}
                onChange={(v) => set({ sortOrder: v ?? 0 })}
              />
            </div>
            <TextInput
              label={t("pro.commands.catalog.field.description")}
              value={draft.description ?? ""}
              onChange={(e) => set({ description: e.target.value })}
            />
          </section>

          <section className={cs.panel}>
            <span className={cs.panelTitle}>{t("pro.commands.catalog.section.delivery")}</span>
            <Select
              label={t("pro.commands.catalog.field.transport")}
              value={draft.transport}
              options={[
                { value: "mqtt", label: t("pro.commands.transport.mqtt") },
                { value: "shadow", label: t("pro.commands.transport.shadow") },
              ]}
              onChange={(transport) => set({ transport })}
            />
            {draft.transport === "mqtt" ? (
              <label className={s.field}>
                <span className={formStyles.label}>{t("pro.commands.catalog.field.template")}</span>
                <textarea className={s.textarea} value={draft.templateText} onChange={(e) => set({ templateText: e.target.value })} spellCheck={false} />
                <span className={s.help}>{t("pro.commands.catalog.field.templateHelp")}</span>
              </label>
            ) : (
              <>
                <div className={s.fieldGrid}>
                  <TextInput label={t("pro.commands.catalog.field.shadowName")} value={draft.shadowName} onChange={(e) => set({ shadowName: e.target.value })} />
                  <Select<"" | "sys_time">
                    label={t("pro.commands.catalog.field.builder")}
                    value={draft.shadowBuilder}
                    options={[
                      { value: "", label: t("pro.commands.catalog.field.builderNone") },
                      { value: "sys_time", label: t("pro.commands.catalog.field.builderSysTime") },
                    ]}
                    onChange={(shadowBuilder) => set({ shadowBuilder })}
                  />
                </div>
                {!draft.shadowBuilder && (
                  <label className={s.field}>
                    <span className={formStyles.label}>{t("pro.commands.catalog.field.datum")}</span>
                    <textarea className={s.textarea} value={draft.datumText} onChange={(e) => set({ datumText: e.target.value })} spellCheck={false} />
                  </label>
                )}
              </>
            )}
          </section>

          <section className={cs.panel}>
            <div className={cs.paramRowHeader}>
              <span className={cs.panelTitle}>{t("pro.commands.catalog.section.params")}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                color="neutral"
                iconLeft={<Plus size={14} />}
                onClick={() =>
                  setParams([
                    ...draft.params,
                    { key: "", label: "", type: "integer", optionsText: "", defaultText: "" },
                  ])
                }
              >
                {t("pro.commands.catalog.param.add")}
              </Button>
            </div>
            {draft.params.length === 0 && <span className={s.muted}>{t("pro.commands.catalog.param.none")}</span>}
            {draft.params.map((param, i) => (
              <ParamEditor
                key={i}
                param={param}
                index={i}
                count={draft.params.length}
                onChange={(next) => setParams(draft.params.map((p, j) => (j === i ? next : p)))}
                onRemove={() => setParams(draft.params.filter((_, j) => j !== i))}
                onMove={(delta) => {
                  const next = [...draft.params];
                  const [moved] = next.splice(i, 1);
                  next.splice(i + delta, 0, moved);
                  setParams(next);
                }}
              />
            ))}
            {draft.transport === "mqtt" && (
              <div className={s.fieldGrid}>
                <Select<"" | "registers" | "batch">
                  label={t("pro.commands.catalog.field.preRead")}
                  value={draft.preRead?.mode ?? ""}
                  options={[
                    { value: "", label: t("pro.commands.catalog.field.preReadNone") },
                    { value: "registers", label: t("pro.commands.catalog.field.preReadRegisters") },
                    { value: "batch", label: t("pro.commands.catalog.field.preReadBatch") },
                  ]}
                  onChange={(mode) =>
                    set({ preRead: mode ? { mode, param: draft.preRead?.param ?? registerValueParams[0]?.key ?? "" } : null })
                  }
                />
                {draft.preRead && (
                  <Select
                    label={t("pro.commands.catalog.field.preReadParam")}
                    value={draft.preRead.param}
                    options={registerValueParams.map((p) => ({ value: p.key, label: p.label || p.key }))}
                    onChange={(param) => set({ preRead: { ...draft.preRead!, param } })}
                  />
                )}
              </div>
            )}
          </section>

          <section className={cs.panel}>
            <span className={cs.panelTitle}>{t("pro.commands.catalog.section.response")}</span>
            <div className={s.fieldGrid}>
              <Select
                label={t("pro.commands.catalog.field.render")}
                value={draft.responseRender}
                options={(["status", "kv", "registers", "raw"] as const).map((value) => ({
                  value,
                  label: t(`pro.commands.render.${value}`),
                }))}
                onChange={(responseRender) => set({ responseRender })}
              />
              <Select
                label={t("pro.commands.catalog.field.success")}
                value={draft.successPredicate}
                options={(["st_if_present", "any", "registers_succ"] as const).map((value) => ({
                  value,
                  label: t(`pro.commands.success.${value}`),
                }))}
                onChange={(successPredicate) => set({ successPredicate })}
              />
              <NumberInput
                label={t("pro.commands.catalog.field.timeout")}
                value={draft.timeoutMs / 1000}
                onChange={(v) => set({ timeoutMs: Math.round((v ?? 10) * 1000) })}
              />
            </div>
          </section>

          <section className={cs.panel}>
            <span className={cs.panelTitle}>{t("pro.commands.catalog.section.safety")}</span>
            <Select
              label={t("pro.commands.catalog.field.danger")}
              value={draft.dangerLevel}
              options={(["normal", "disruptive", "irreversible"] as const).map((value) => ({
                value,
                label: t(`pro.commands.danger.${value}`),
              }))}
              onChange={(dangerLevel) => set({ dangerLevel })}
            />
            {draft.dangerLevel !== "normal" && (
              <TextInput
                label={t("pro.commands.catalog.field.confirmation")}
                value={draft.confirmationText ?? ""}
                onChange={(e) => set({ confirmationText: e.target.value })}
              />
            )}
            <span className={s.help}>{t("pro.commands.catalog.field.dangerHelp")}</span>
          </section>

          <section className={cs.panel}>
            <span className={cs.panelTitle}>{t("pro.commands.catalog.section.preconditions")}</span>
            <div className={s.fieldGrid}>
              <TextInput
                label={t("pro.commands.catalog.field.wcmMinFw")}
                value={draft.preconditions.wcmMinFw ?? ""}
                placeholder="2.1.0"
                onChange={(e) => setPre({ wcmMinFw: e.target.value })}
              />
              <TextInput
                label={t("pro.commands.catalog.field.wcmMaxFw")}
                value={draft.preconditions.wcmMaxFw ?? ""}
                onChange={(e) => setPre({ wcmMaxFw: e.target.value })}
              />
              <NumberInput
                label={t("pro.commands.catalog.field.maxInverters")}
                value={draft.preconditions.maxInverters}
                onChange={(maxInverters) => setPre({ maxInverters })}
              />
              <TextInput
                label={t("pro.commands.catalog.field.requiresComponent")}
                value={draft.preconditions.requiresComponentType ?? ""}
                placeholder="pcs"
                onChange={(e) => setPre({ requiresComponentType: e.target.value })}
              />
            </div>
            <div className={s.inlineRow} style={{ gap: 20 }}>
              <span className={formStyles.label}>{t("pro.commands.catalog.field.communicators")}</span>
              {(["wcm", "ems-c"] as const).map((type) => {
                const current = draft.preconditions.communicatorTypes ?? [];
                return (
                  <label key={type} className={s.inlineRow}>
                    <Checkbox
                      checked={current.includes(type)}
                      onCheckedChange={(checked) =>
                        setPre({
                          communicatorTypes: checked ? [...current, type] : current.filter((c) => c !== type),
                        })
                      }
                    />
                    <span>{type.toUpperCase()}</span>
                  </label>
                );
              })}
            </div>
            <span className={s.help}>{t("pro.commands.catalog.field.preconditionsHelp")}</span>
          </section>

          <div className={cs.footer}>
            {!isNew ? (
              <Button type="button" color="danger" variant="outline" onClick={handleDelete} disabled={saving}>
                {t("shared.common.delete")}
              </Button>
            ) : (
              <span />
            )}
            <div className={s.inlineRow}>
              <Button type="button" color="neutral" onClick={back} disabled={saving}>
                {t("shared.common.cancel")}
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={handleSave}
                disabled={saving || !draft.key.trim() || !draft.displayName.trim() || !draft.category.trim()}
              >
                {saving ? t("pro.commands.catalog.saving") : t("shared.common.save")}
              </Button>
            </div>
          </div>
        </div>

        {token && <PreviewPanel draft={draft} token={token} />}
      </div>
      <ConfirmModal />
    </div>
  );
}
